/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import {
  App,
  Col,
  Row,
  TableColumnsType,
  Typography,
  Button as ButtonAnt,
  Tooltip
} from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Button } from '@/components/button';
import { UserAdminSearcher } from '@/components/forms/user-admin-searcher.form';
import { Table } from '@/components/tables/table';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  getListAdminUser,
  watchListAdminUser,
  watchAdminUserPagination,
  watchAdminUserLoading,
  deleteAdminUser,
  resetMessages,
  watchAdminUserMessage
} from '@/redux/slices/admin.slice';
import { useQuery } from '@/hooks/query.hook';
import { PermissionMode } from '@/enums/mode.enum';
import { PageURLs } from '@/utils/navigate';
import { useCallback, useEffect, useState } from 'react';
import { PAGINATION_PARAMS } from '@/constants/pagination.constant';
import { tableScrollY } from '@/utils/common';
import { transformerArray } from '@/redux/transformer';
import { setMessages } from '@/redux/slices/app.slice';
import usePermission from '@/hooks/permission.hook';
import { watchRoleList } from '@/redux/slices/roles.slice';

type PageChangeParams = {
  page?: number;
  pageSize?: number;
  filter?: FilterAdminPermissions;
};

export const AdminPermissionsTable = () => {
  const { modal } = App.useApp();
  const { push } = useQuery();
  const dispatch = useAppDispatch();
  const listUserAdmin = useAppSelector(watchListAdminUser);
  const pagination = useAppSelector(watchAdminUserPagination);
  const loading = useAppSelector(watchAdminUserLoading);
  const messageInfo = useAppSelector(watchAdminUserMessage);
  const roles = useAppSelector(watchRoleList);

  const { creatable, updatable, deletable } = usePermission();

  const [filter, setFilter] = useState<FilterAdminPermissions>({});

  const handleSearch = (values: FilterAdminPermissions) => {
    const newFilter = {
      ...filter,
      ...values
    };
    setFilter(newFilter);
    handlePageChange({ filter: newFilter });
  };

  const handleDeleteUser = async (roleId: number) => {
    await dispatch(deleteAdminUser(roleId));
    const remainingRecords = listUserAdmin.length - 1;
    if (remainingRecords === 0 && pagination.curPage > 1) {
      handlePageChange({
        page: pagination.curPage - 1,
        pageSize: pagination.limitPage,
        filter
      });
    } else {
      handlePageChange({
        page: pagination.curPage,
        pageSize: pagination.limitPage,
        filter
      });
    }
  };

  const confirmDeleteAdmin = (userId: number) => {
    modal.confirm({
      title: 'Bạn có chắc chắn xoá quản trị viên này không?',
      content: (
        <em>
          Người này sẽ không thể truy cập vào CMS (trang quản lý nội dung của
          PMS) này được nữa
        </em>
      ),
      okText: 'Xác nhận',
      width: 450,
      cancelText: 'Hủy',
      onOk: () => handleDeleteUser(userId)
    });
  };

  const handleAddAdmin = () => {
    push(`${PageURLs.ofAdminManagement()}?mode=${PermissionMode.CREATE}`);
  };

  const handleEditAdmin = (adminId: string) => {
    push(
      `${PageURLs.ofAdminManagement()}?mode=${PermissionMode.EDIT}&adminId=${adminId}`
    );
  };

  const handlePageChange = useCallback(
    ({
      page = PAGINATION_PARAMS.offset,
      pageSize = PAGINATION_PARAMS.limit,
      filter
    }: PageChangeParams) => {
      dispatch(
        getListAdminUser({
          offset: page,
          limit: pageSize,
          roleId: filter?.roleId,
          search: filter?.textSearch
        })
      );
    },
    [dispatch]
  );

  const handleReset = () => {
    handlePageChange({ filter });
  };

  useEffect(() => {
    handlePageChange({});
  }, [handlePageChange]);

  useEffect(() => {
    if (messageInfo) {
      dispatch(
        setMessages([
          {
            type: messageInfo.type,
            message: messageInfo.message,
            description: messageInfo.description
          }
        ])
      );
      dispatch(resetMessages());
    }
  }, [dispatch, messageInfo]);

  const columns: TableColumnsType<User> = [
    {
      title: 'Email',
      width: 100,
      dataIndex: 'email',
      key: 'email',
      fixed: 'left'
    },
    {
      title: 'Vai trò',
      dataIndex: 'role',
      key: 'role',
      render: (record) => {
        if (!record) {
          return null;
        }
        const role = transformerArray(record) as Role[];
        const rolePermission = role.map((item) => {
          const fullRole = roles.find((it) => it.id === item.id);

          return fullRole?.module.map((mdl) => {
            const permission = mdl.permission.map((pm) => pm.name).join(', ');
            return `${mdl.name} - ${permission}`;
          });
        });

        const uniqueRolePermission = [...new Set(rolePermission.flat())];

        return (
          <Tooltip
            placement='right'
            title={<div style={{ color: 'black' }}>{uniqueRolePermission.join('\n')}</div>}
            overlayInnerStyle={{ whiteSpace: 'pre-line' }}
            overlayStyle={{ maxWidth: 600 }}
            color='#fff'
          >
            {role.map((roleItem) => roleItem.name).join(', ')}
          </Tooltip>
        );
      }
    },
    {
      title: 'Tên quản trị viên',
      dataIndex: 'fullName',
      key: 'fullName',
      width: 200,
      render: (fullName: string | undefined) => {
        return fullName ? fullName : '-';
      }
    },
    {
      title: 'Thao tác',
      key: 'action',
      fixed: 'right',
      width: 104,
      render: (record) => (
        <Row gutter={[8, 10]}>
          {updatable && (
            <Col>
              <Tooltip title='Cập nhật'>
                <ButtonAnt
                  onClick={() => handleEditAdmin(record.id)}
                  type='primary'
                  icon={<EditOutlined />}
                />
              </Tooltip>
            </Col>
          )}
          {deletable && (
            <Col>
              <Tooltip title='Xoá'>
                <ButtonAnt
                  onClick={() => confirmDeleteAdmin(record.id)}
                  type='primary'
                  danger
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Col>
          )}
        </Row>
      )
    }
  ];

  return (
    <div css={rootStyles}>
      <Row css={boxStyles} justify='space-between' align='middle'>
        <Col>
          <Typography.Title css={titleStyles} level={5}>
            Quản lý quản trị viên
          </Typography.Title>
        </Col>
        {creatable && (
          <Col>
            <Button
              onClick={handleAddAdmin}
              type='primary'
              icon={<PlusOutlined />}
              iconPosition='start'
            >
              Thêm mới quản trị viên
            </Button>
          </Col>
        )}
      </Row>
      <div css={boxStyles}>
        <UserAdminSearcher onSearch={handleSearch} />
      </div>
      <div css={tableStyles}>
        <Table<User>
          rowKey={(record) => record.id}
          columns={columns}
          dataSource={listUserAdmin}
          placeholder='Nhập email/tên quản trị viên để tìm kiếm'
          loading={loading}
          totalResult={pagination.totalRows}
          scrollY={tableScrollY(pagination.limitPage)}
          pagination={{
            showQuickJumper: false,
            showSizeChanger: true,
            current: pagination.curPage,
            pageSize: pagination.limitPage,
            total: pagination.totalRows,
            onChange: (page, pageSize) => {
              handlePageChange({ page, pageSize, filter });
            }
          }}
          onFilter={handleSearch}
          onReset={handleReset}
        />
      </div>
    </div>
  );
};

const rootStyles = css`
  background: var(--gray-soft-color);
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  padding: 1.4rem 2.4rem;
  min-height: calc(100vh - var(--header-height));
`;

const boxStyles = css`
  padding: 1rem;
  background: var(--white-color);
  border-radius: 1rem;
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }
`;

const tableStyles = css`
  background: var(--white-color);
  border-radius: 1rem;
  padding: 0 1rem;
  .table-top {
    padding: 1rem 0;
  }
`;

const titleStyles = css`
  margin-bottom: 0 !important;
  line-height: 3.2rem !important;
`;
